/**
 * useRtcStats — poll a tile's WebRTC stats for the "stats for nerds" panel.
 *
 * Given a participant's video producerId (and whether it's the self tile), it
 * resolves the mediasoup-client consumer (or our own producer for self) via
 * mediaService, polls getStats() ~once/second while `enabled`, walks the
 * RTCStatsReport, and returns a normalized snapshot whose fields map 1:1 onto
 * the panel rows (YouTube "Stats for nerds" layout, honest WebRTC labels).
 *
 * Rates (bitrate / network activity / fps fallback) are computed from deltas
 * between consecutive samples, so the FIRST tick shows mostly counters and the
 * second tick onward shows live rates.
 */
import { useEffect, useRef, useState } from 'react';
import mediaService from '../services/mediaService';
import remoteAudio from '../services/remoteAudio';

const POLL_MS = 1000;

// Walk an RTCStatsReport (a Map) and pull the one entry of a given type that
// matters. For inbound/outbound-rtp we want the VIDEO entry (kind==='video' or
// mediaType, falling back to a frame-bearing entry).
function pickRtp(report, type) {
  // A simulcast SENDER emits one outbound-rtp per layer (rid r0/r1/r2); a watcher
  // gets one inbound-rtp. Among the video entries, prefer the one actually
  // carrying media — the highest layer that's encoding — rather than blindly the
  // last (the top layer may sit at 0 until the source/bandwidth feeds it). Rank
  // by bytes (sent or received), tie-broken by frame area.
  const candidates = [];
  report.forEach((s) => {
    if (s.type !== type) return;
    const kind = s.kind || s.mediaType;
    if (kind === 'video' || (!kind && (s.frameWidth || s.framesDecoded || s.framesEncoded))) {
      candidates.push(s);
    }
  });
  if (candidates.length <= 1) return candidates[0] || null;
  const weight = (s) =>
    (s.bytesReceived || s.bytesSent || 0) * 1e6 +
    (s.frameWidth || 0) * (s.frameHeight || 0);
  return candidates.reduce((best, s) => (weight(s) > weight(best) ? s : best));
}

function pickAudioRtp(report, type) {
  let chosen = null;
  report.forEach((s) => {
    if (s.type !== type) return;
    if ((s.kind || s.mediaType) === 'audio') chosen = s;
  });
  return chosen;
}

// The in-use ICE candidate pair (for connection speed + RTT). Prefer the one
// flagged nominated/selected; otherwise the first succeeded pair.
function pickCandidatePair(report) {
  let selected = null;
  let firstSucceeded = null;
  report.forEach((s) => {
    if (s.type !== 'candidate-pair') return;
    if (s.selected || s.nominated) selected = s;
    if (!firstSucceeded && s.state === 'succeeded') firstSucceeded = s;
  });
  return selected || firstSucceeded;
}

function codecMime(report, codecId) {
  if (!codecId) return null;
  const c = report.get ? report.get(codecId) : null;
  return c ? c.mimeType : null;
}

// "video/VP8" -> "VP8"; tolerate missing.
const shortCodec = (mime) => (mime ? mime.split('/')[1] || mime : null);

const fmtBitrate = (bps) => {
  if (bps == null || !isFinite(bps)) return '—';
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  return `${Math.round(bps / 1000)} Kbps`;
};

const fmtRate = (bytesPerSec) => {
  if (bytesPerSec == null || !isFinite(bytesPerSec)) return '—';
  if (bytesPerSec >= 1_000_000) return `${(bytesPerSec / 1_000_000).toFixed(2)} MB/s`;
  return `${(bytesPerSec / 1000).toFixed(1)} KB/s`;
};

const shortId = (id) => (id ? `${String(id).slice(0, 8)}…` : '—');

export default function useRtcStats({ producerId, isSelf, viewEl, enabled, userId }) {
  const [snapshot, setSnapshot] = useState(null);
  // Previous sample for delta-based rates: { ts, bytes, frames }.
  const prevRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      prevRef.current = null;
      setSnapshot(null);
      return undefined;
    }

    let timer = null;
    let cancelled = false;

    const tick = async () => {
      try {
        const handles = isSelf
          ? mediaService.getProducerStatsHandles()
          : mediaService.getConsumerStatsHandles(producerId);

        if (!handles) {
          if (!cancelled) setSnapshot({ unavailable: true });
          return;
        }

        const videoObj = isSelf ? handles.videoProducer : handles.videoConsumer;
        const audioObj = isSelf ? handles.audioProducer : handles.audioConsumer;
        if (!videoObj || typeof videoObj.getStats !== 'function') {
          if (!cancelled) setSnapshot({ unavailable: true });
          return;
        }

        const rtpType = isSelf ? 'outbound-rtp' : 'inbound-rtp';
        const report = await videoObj.getStats();
        const rtp = pickRtp(report, rtpType) || {};
        const pair = pickCandidatePair(report) || {};

        // Codec rows: prefer the static rtpParameters (instant, no sample needed),
        // fall back to the codec stat resolved via codecId. rtpParameters reflects
        // what was negotiated, so it surfaces VP9 / H264 / VP8 immediately — but
        // the live `getStats()` codec entry is the source of truth for what's
        // ACTUALLY flowing, so prefer it for the name when present.
        const vidParamCodec = videoObj.rtpParameters?.codecs?.[0];
        const liveCodecMime = codecMime(report, rtp.codecId);
        const videoCodecName =
          shortCodec(liveCodecMime) || shortCodec(vidParamCodec?.mimeType) || '—';
        const videoPt = vidParamCodec?.payloadType;

        // Scalability: how the 3 spatial layers are carried. VP9 uses K-SVC (a
        // single encoding with scalabilityMode L3T3_KEY); VP8/H264 use 3-tier
        // simulcast (3 separate encodings). For the SELF tile this is exact from
        // our own producer's encodings. For a WATCHED tile the consumer only sees
        // one decoded stream, so we infer SVC-vs-simulcast from the codec (VP9 =>
        // SVC in our pipeline) and surface the encoder's mode from stats if given.
        let scalability = '—';
        const encs = videoObj.rtpParameters?.encodings || [];
        const svcMode =
          encs.find((e) => e && e.scalabilityMode)?.scalabilityMode ||
          rtp.scalabilityMode || // some browsers report it on outbound-rtp
          null;
        if (isSelf) {
          if (svcMode) scalability = `${svcMode} (SVC)`;
          else if (encs.length > 1) scalability = `${encs.length}-layer simulcast`;
          else if (encs.length === 1) scalability = 'single layer';
        } else if (svcMode) {
          scalability = `${svcMode} (SVC)`;
        } else if (/vp9/i.test(videoCodecName)) {
          scalability = 'SVC';
        } else if (videoCodecName !== '—') {
          scalability = 'simulcast';
        }

        let audioCodecName = '—';
        let audioPt;
        let audioLevel = null;
        if (audioObj) {
          const aParamCodec = audioObj.rtpParameters?.codecs?.[0];
          audioCodecName = shortCodec(aParamCodec?.mimeType) || '—';
          audioPt = aParamCodec?.payloadType;
          try {
            const aReport = await audioObj.getStats();
            const aRtp = pickAudioRtp(aReport, rtpType);
            if (aRtp && aRtp.audioLevel != null) audioLevel = aRtp.audioLevel;
            if (!aParamCodec) {
              audioCodecName = shortCodec(codecMime(aReport, aRtp?.codecId)) || audioCodecName;
            }
          } catch (e) { /* audio stats optional */ }
        }

        // --- delta-based rates ---
        const now = rtp.timestamp || (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const bytes = isSelf ? rtp.bytesSent : rtp.bytesReceived;
        const framesNow = isSelf ? rtp.framesEncoded : rtp.framesDecoded;
        let bitrateBps = null;
        let bytesPerSec = null;
        let fpsFromDelta = null;
        const prev = prevRef.current;
        if (prev && now > prev.ts) {
          const dt = (now - prev.ts) / 1000; // seconds
          if (bytes != null && prev.bytes != null) {
            bitrateBps = ((bytes - prev.bytes) * 8) / dt;
            bytesPerSec = (bytes - prev.bytes) / dt;
          }
          if (framesNow != null && prev.frames != null) {
            fpsFromDelta = (framesNow - prev.frames) / dt;
          }
        }
        prevRef.current = { ts: now, bytes, frames: framesNow };

        const fps =
          rtp.framesPerSecond != null
            ? Math.round(rtp.framesPerSecond)
            : fpsFromDelta != null
            ? Math.round(fpsFromDelta)
            : null;

        // Frames / dropped. Self has no "dropped" (encode side) so omit count.
        const framesTotal = framesNow;
        const framesDropped = isSelf ? null : rtp.framesDropped;

        // Buffer health: jitter buffer in seconds (RTC has no big prebuffer).
        let jitterBufferSec = null;
        if (!isSelf && rtp.jitterBufferDelay != null && rtp.jitterBufferEmittedCount) {
          jitterBufferSec = rtp.jitterBufferDelay / rtp.jitterBufferEmittedCount;
        }

        // Packet loss (inbound only).
        const packetsLost = isSelf ? null : rtp.packetsLost;
        const packetsReceived = rtp.packetsReceived;
        let lossPct = null;
        if (!isSelf && packetsLost != null && packetsReceived != null) {
          const total = packetsLost + packetsReceived;
          if (total > 0) lossPct = (packetsLost / total) * 100;
        }

        const rttMs =
          pair.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null;
        const availBps = isSelf ? pair.availableOutgoingBitrate : pair.availableIncomingBitrate;

        // Debug blob (the "mystery text" analogue).
        const debugParts = [];
        if (rttMs != null) debugParts.push(`rtt:${rttMs}ms`);
        if (rtp.jitter != null) debugParts.push(`jit:${(rtp.jitter * 1000).toFixed(0)}ms`);
        debugParts.push(`nack:${rtp.nackCount ?? 0} pli:${rtp.pliCount ?? 0} fir:${rtp.firCount ?? 0}`);
        if (!isSelf && rtp.keyFramesDecoded != null) debugParts.push(`kf:${rtp.keyFramesDecoded}`);
        if (isSelf && rtp.keyFramesEncoded != null) debugParts.push(`kf:${rtp.keyFramesEncoded}`);
        if (rtp.rid) debugParts.push(`rid:${rtp.rid}`);
        if (bytes != null) debugParts.push(`${isSelf ? 'sent' : 'recv'}:${(bytes / 1_000_000).toFixed(2)}MB`);

        const snap = {
          unavailable: false,
          isSelf,
          // 1. Producer / Consumer ID
          producerId: producerId ? shortId(producerId) : (isSelf ? shortId(mediaService.getOwnProducerId()) : '—'),
          consumerId: isSelf ? '(self/producer)' : shortId(videoObj.id),
          // 2. Viewport / Frames
          viewport:
            viewEl && viewEl.clientWidth
              ? `${viewEl.clientWidth}×${viewEl.clientHeight}`
              : '—',
          frames:
            framesTotal != null
              ? isSelf
                ? `${framesTotal} encoded`
                : `${framesDropped ?? 0} dropped of ${framesTotal}`
              : '—',
          // 3. Current / Optimal Res
          currentRes:
            rtp.frameWidth && rtp.frameHeight
              ? `${rtp.frameWidth}×${rtp.frameHeight}${fps != null ? `@${fps}` : ''}`
              : '—',
          optimalRes: '≤1920×1080@60',
          // 4. Volume / Audio level. For remote users the audible level is
          // element volume (native slider, 0-100%) × the listening-chain boost
          // (0-400%, remoteAudio) — show both factors.
          volume: isSelf
            ? '(local)'
            : viewEl && typeof viewEl.volume === 'number'
            ? `${Math.round(viewEl.volume * 100)}%${
                userId ? ` × ${remoteAudio.getSettings(userId).volume}% boost` : ''
              }`
            : '—',
          audioLevel: audioLevel != null ? `${Math.round(audioLevel * 100)}` : '—',
          // 5. Codecs — "VP8 (96) / opus (100)"
          codecs: `${videoCodecName}${videoPt != null ? ` (${videoPt})` : ''} / ${audioCodecName}${
            audioPt != null ? ` (${audioPt})` : ''
          }`,
          // 5b. Dedicated video-codec + scalability rows (VP9 SVC vs VP8/H264
          // simulcast — answers "what is the signal" at a glance).
          videoCodec: videoCodecName,
          scalability,
          // 7. Connection speed
          connectionSpeed: fmtBitrate(availBps != null ? availBps : bitrateBps),
          // 8. Network activity
          networkActivity: fmtRate(bytesPerSec),
          // 9. Buffer health
          bufferHealth:
            jitterBufferSec != null
              ? `${jitterBufferSec.toFixed(3)} s${rtp.freezeCount ? ` · ${rtp.freezeCount} freezes` : ''}`
              : isSelf
              ? '(n/a sender)'
              : '—',
          // 10. Loss + debug
          packetLoss:
            lossPct != null ? `${lossPct.toFixed(2)}% (${packetsLost})` : isSelf ? '(n/a sender)' : '—',
          debug: debugParts.join(' '),
          // 11. Date
          date: new Date().toString(),
        };

        if (!cancelled) setSnapshot(snap);
      } catch (e) {
        if (!cancelled) setSnapshot({ unavailable: true, error: e.message });
      }
    };

    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      prevRef.current = null;
    };
  }, [producerId, isSelf, viewEl, enabled, userId]);

  return snapshot;
}
